import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Broadcast — an organizer-composed email to a segment of the organization
 * (ADR 0053). The document is the send record (there is no other email log):
 * status + counts are updated by the communications queue worker as the
 * fan-out progresses. `channel` reserves the slot for SMS later.
 */

export const BroadcastSegmentSchema = z.object({
  scope: z.enum(['organization', 'registration', 'contest', 'division']),
  // Required for every scope except 'organization'.
  targetId: z.string().optional(),
  // managers = team contacts only; everyone = managers + all rostered players.
  audience: z.enum(['managers', 'everyone']),
});

export type BroadcastSegment = z.infer<typeof BroadcastSegmentSchema>;

export const BroadcastSchema = z.object({
  _id: objectId,
  organization: objectId,
  createdBy: objectId, // Admin
  segment: BroadcastSegmentSchema,
  subject: z.string().min(1).max(200),
  // 'html' = Quill-authored HTML (compose page, INT-0015); absent/'text' =
  // legacy plain text. The worker renders accordingly (utils/broadcastHtml).
  body: z.string().min(1).max(50000),
  bodyFormat: z.enum(['text', 'html']).optional(),
  channel: z.enum(['email']).default('email'),
  status: z.enum(['draft', 'queued', 'sending', 'sent', 'failed']).default('draft'),
  recipientCount: z.number().optional(),
  sentCount: z.number().optional(),
  failedCount: z.number().optional(),
  sentAt: z.coerce.date().optional(),
  ...timestampFields,
});

export type Broadcast = z.infer<typeof BroadcastSchema>;

export const broadcastMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  organization: { ref: 'Organization', hidden: true },
  createdBy: { ref: 'Admin', label: 'Sent By' },
  segment: { label: 'Segment' },
  subject: { label: 'Subject' },
  body: { label: 'Body' },
  bodyFormat: { hidden: true },
  channel: { label: 'Channel', hidden: true },
  status: { label: 'Status' },
  recipientCount: { label: 'Recipients' },
  sentCount: { label: 'Sent' },
  failedCount: { label: 'Failed' },
  sentAt: { label: 'Sent At' },
  created: { hidden: true },
  updated: { hidden: true },
};
